import { ListSubheader, Menu, type MenuProps } from '@mui/material';
import { useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useRecoilValue } from 'recoil';
import MenuChoice from '~/components/Menu/MenuChoice';
import {
    AVAILABLE_ROI_TYPES,
    ROI_POINT_TYPES,
    RoiInterpretedType,
} from '~/domain/dicom/dicomstructureset';
import {
    findExternalRoiReadModel,
    RoiReadModel,
} from '~/domain/readmodels/StructureSetReadModel';
import { currentStructureSetState } from '~/state/atoms/structureSet/structureSetAtoms';

/**
 * The menu that changes a structure's interpreted type: the types of the
 * structure's category (point or volume), the current one checked, and the
 * current type first when it is outside that list. EXTERNAL is disabled
 * while another structure of the set is the EXTERNAL, with a reason that
 * names that structure; the domain refuses a second one whatever the
 * caller. Where the menu opens is the caller's, through the Menu's position
 * props.
 */
export default function StructureTypeMenu({
    roi,
    onChange,
    onClose,
    ...position
}: {
    roi: RoiReadModel;
    onChange: (value: RoiInterpretedType) => void;
    onClose: () => void;
} & Pick<
    MenuProps,
    | 'open'
    | 'anchorEl'
    | 'anchorReference'
    | 'anchorPosition'
    | 'anchorOrigin'
    | 'transformOrigin'
    | 'marginThreshold'
>) {
    const intl = useIntl();
    const structureSet = useRecoilValue(currentStructureSetState);
    const otherExternal = findExternalRoiReadModel(structureSet, roi.roiNumber);

    const allowedTypes = useMemo(() => {
        const categoryTypes = roi.isPointRoi
            ? ROI_POINT_TYPES
            : AVAILABLE_ROI_TYPES.filter((t) => !ROI_POINT_TYPES.includes(t));
        return categoryTypes.includes(roi.interpretedType)
            ? categoryTypes
            : [roi.interpretedType, ...categoryTypes];
    }, [roi.interpretedType, roi.isPointRoi]);

    return (
        <Menu
            {...position}
            onClose={onClose}
            data-testid="structure-type-menu"
            slotProps={{ paper: { sx: { width: 260, mt: 0 } } }}
        >
            <ListSubheader>
                <FormattedMessage id="structure.type.change" />
            </ListSubheader>
            {allowedTypes.map((value) => (
                <MenuChoice
                    key={value || 'UNKNOWN'}
                    name={value || intl.formatMessage({ id: 'unknown' })}
                    selected={value === roi.interpretedType}
                    disabledReason={
                        value === RoiInterpretedType.EXTERNAL &&
                        roi.interpretedType !== RoiInterpretedType.EXTERNAL &&
                        otherExternal
                            ? intl.formatMessage(
                                  { id: 'structure.externalTaken' },
                                  { name: otherExternal.name }
                              )
                            : undefined
                    }
                    onClick={() => {
                        onClose();
                        if (value !== roi.interpretedType) {
                            onChange(value);
                        }
                    }}
                />
            ))}
        </Menu>
    );
}
